import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// withCredentials (KAN-531): the refresh token lives in an httponly cookie that only the
// browser can send. Same-origin builds (REACT_APP_API_URL=/api) send it anyway; a local dev
// build on another port (localhost:3000 -> localhost:8000) needs this flag both to keep the
// cookie the login response sets and to send it to /auth/refresh. The server already allows
// credentials for its listed origins.
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Request interceptor to add Authorization header
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// KAN-531: a 401 used to sign the learner out at once, so everyone was sent to /login when
// the 60-minute access token ran out, even mid-challenge. Now a 401 on an ordinary request
// asks POST /auth/refresh for a new access token and retries the request once. The refresh
// token is an httponly cookie (path /api/auth) that JavaScript never sees; this code only
// stores the new access token, in the same place login does. If the refresh fails, or the
// retried request is refused again, the learner is signed out as before.
//
// These routes never trigger a refresh: they either issue tokens themselves or end the
// session, so refreshing around them could only loop or undo a sign-out.
const NO_REFRESH_ROUTE = /(^|\/)auth\/(login|register|refresh|logout|logout-all|webauthn\/login\/(begin|complete))\/?(\?|$)/;

export const isNoRefreshRoute = (url = '') => NO_REFRESH_ROUTE.test(url);

const signOut = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  window.location.href = '/login';
};

// Single flight: every 401 that arrives while a refresh is running waits for that one call,
// so the one-time-use refresh token is presented once, not once per failed request.
let refreshInFlight = null;

// Two tabs (KAN-531 follow-up). sessionStorage is per tab but the refresh cookie is shared.
// The server rotates the refresh token on every use and answers 401 to the old one (the
// session itself stays live). So when two tabs refreshed at the same moment, both sent the
// same cookie, one won, and the other was signed out.
//
// Fix: the refresh call runs under a browser-wide Web Lock, so tabs refresh one at a time.
// The tab that waits then sends the new cookie the first tab's refresh set, and succeeds.
// The lock holds only its name; no token goes into any shared storage.
// Browsers without the Web Locks API fall back to one retry of the refresh after a short
// pause, by which time the other tab's response has set the new cookie.
export const REFRESH_LOCK_NAME = 'fc-auth-refresh';
export const REFRESH_RETRY_DELAY_MS = 500;

// _skipSignOut: a failed refresh must not sign out from inside the interceptor, because the
// fallback below may still retry it. The caller signs out if the refresh finally fails.
const postRefresh = () =>
  api.post('/auth/refresh', undefined, { _skipSignOut: true }).then((res) => {
    const newToken = res.data && res.data.access_token;
    if (!newToken) throw new Error('Refresh returned no access token');
    sessionStorage.setItem('token', newToken);
    return newToken;
  });

const webLocks = () =>
  (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function')
    ? navigator.locks
    : null;

const refreshAcrossTabs = () => {
  const locks = webLocks();
  if (locks) {
    return locks.request(REFRESH_LOCK_NAME, () => postRefresh());
  }
  return postRefresh().catch(async (err) => {
    if (err?.response?.status !== 401) throw err;
    await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
    return postRefresh();
  });
};

export const refreshAccessToken = () => {
  if (!refreshInFlight) {
    refreshInFlight = refreshAcrossTabs().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Response interceptor for 401 handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }
    // The caller handles this 401 itself (the refresh call above, and logout, which clears
    // local state on its own and must not jump to /login).
    if (config && config._skipSignOut) {
      return Promise.reject(error);
    }
    // Auth routes, a request already retried once, or no request to retry: sign out.
    if (!config || config._authRetried || isNoRefreshRoute(config.url)) {
      signOut();
      return Promise.reject(error);
    }
    config._authRetried = true;
    try {
      await refreshAccessToken();
    } catch (refreshError) {
      signOut();
      return Promise.reject(error);
    }
    // The request interceptor puts the new token from sessionStorage on the retry.
    return api(config);
  }
);

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) =>
    api.post('/auth/reset-password', { token, new_password }),
  // ── Security port: password / MFA / passkeys / sessions / email change ──
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
  mfaSetup: () => api.post('/auth/mfa/setup'),
  mfaEnable: (code) => api.post('/auth/mfa/enable', { code }),
  mfaDisable: (password, code) => api.post('/auth/mfa/disable', { password, code }),
  regenerateRecoveryCodes: (code) => api.post('/auth/mfa/recovery-codes', { code }),
  webauthnRegisterBegin: () => api.post('/auth/webauthn/register/begin'),
  webauthnRegisterComplete: (credential, name) =>
    api.post('/auth/webauthn/register/complete', { credential, name }),
  webauthnLoginBegin: (email) => api.post('/auth/webauthn/login/begin', { email }),
  webauthnLoginComplete: (email, credential) =>
    api.post('/auth/webauthn/login/complete', { email, credential }),
  webauthnListCredentials: () => api.get('/auth/webauthn/credentials'),
  webauthnDeleteCredential: (id) => api.delete(`/auth/webauthn/credentials/${id}`),
  listSessions: () => api.get('/auth/sessions'),
  revokeSession: (id) => api.delete(`/auth/sessions/${id}`),
  // _skipSignOut: AuthContext.logout clears local state itself, whatever the server says.
  logout: () => api.post('/auth/logout', undefined, { _skipSignOut: true }),
  logoutAll: () => api.post('/auth/logout-all'),
  requestEmailChange: (new_email, password) =>
    api.post('/auth/email-change/request', { new_email, password }),
  confirmEmailChange: (token) => api.post('/auth/email-change/confirm', { token }),
  verifyEmail: (token) => api.get('/auth/verify-email', { params: { token } }),
  // KAN-443: the server answers the same way whether or not the address exists.
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};

// User endpoints
export const usersAPI = {
  getMe: () => api.get('/users/me'),
  getProfile: () => api.get('/users/me'),           // alias
  getStats: () => api.get('/users/me'),              // alias (same endpoint returns stats)
  updateProfile: (data) => api.put('/users/me/profile', data),
  changePassword: (data) => api.post('/users/me/change-password', data),
  getPublicUser: (id) => api.get(`/users/${id}/public`),
  getPublicProfile: (id) => api.get(`/users/${id}/public`), // alias
  getPublicRecords: (id) => api.get(`/sjp/users/${id}`), // SJP records for public user
  listUsers: (params) => api.get('/admin/users', { params }), // admin alias
  // GDPR (security port)
  exportData: () => api.get('/users/me/export'),
  deleteAccount: (password) => api.delete('/users/me', { data: { password } }),
};

// Test endpoints
export const testsAPI = {
  startTest: () => api.get('/tests/start'),
  start: () => api.get('/tests/start'),              // alias
  submitTest: (sessionId, answers) =>
    api.post(`/tests/${sessionId}/submit`, { answers }),
  submit: (sessionId, answers) =>
    api.post(`/tests/${sessionId}/submit`, { answers }), // alias
  getResult: (sessionId) => api.get(`/tests/${sessionId}/result`),
  // The backend lists a learner's sessions at /tests/me/test-sessions (there is
  // no /tests/history route); the dashboard reads FC completion from this.
  mySessions: () => api.get('/tests/me/test-sessions'),
  getTestHistory: () => api.get('/tests/me/test-sessions'),
  getHistory: () => api.get('/tests/me/test-sessions'),       // alias
};

// New Words endpoints
export const newWordsAPI = {
  create: (data) => api.post('/new-words', data),
  list: (params) => api.get('/new-words', { params }),
  listNewWords: (params) => api.get('/new-words', { params }), // alias
  listMy: (params) => api.get('/new-words', { params: { ...params, my: true } }),
  get: (id) => api.get(`/new-words/${id}`),
  getDetail: (id) => api.get(`/new-words/${id}`),   // alias
  update: (id, data) => api.put(`/new-words/${id}`, data),
  like: (id) => api.post(`/new-words/${id}/like`),
  unlike: (id) => api.delete(`/new-words/${id}/like`),
  updateWordStatus: (id, status) =>
    api.put(`/admin/new-words/${id}/status`, { status }), // admin alias
};

// Learning Theories endpoints
export const learningTheoriesAPI = {
  create: (data) => api.post('/learning-theories', data),
  list: (params) => api.get('/learning-theories', { params }),
  get: (id) => api.get(`/learning-theories/${id}`),
  getDetail: (id) => api.get(`/learning-theories/${id}`), // alias
  update: (id, data) => api.put(`/learning-theories/${id}`, data),
  like: (id) => api.post(`/learning-theories/${id}/like`),
  unlike: (id) => api.delete(`/learning-theories/${id}/like`),
};

// Challenges endpoints
export const challengesAPI = {
  list: (params) => api.get('/challenges', { params }),
  get: (id) => api.get(`/challenges/${id}`),
  getDetail: (id) => api.get(`/challenges/${id}`),  // alias
  submitAnswer: (id, data) => api.post(`/challenges/${id}/answers`, data),
  listAnswers: (id, params) =>
    api.get(`/challenges/${id}/answers`, { params }),
  // KAN-443: the served routes are /api/challenges/answers/{answer_id}... and take the ANSWER id.
  // There is no unlike route on the server, so there is no unlikeAnswer here.
  likeAnswer: (answerId) => api.post(`/challenges/answers/${answerId}/like`),
  updateAnswer: (answerId, data) => api.put(`/challenges/answers/${answerId}`, data),
};

// Invites endpoints
export const invitesAPI = {
  getMe: () => api.get('/invites/me'),
  listMyInvites: () => api.get('/invites/me'),       // alias
  generate: () => api.post('/invites/generate'),
  generateCode: () => api.post('/invites/generate'), // alias
  getRecords: () => api.get('/invites/records'),
  validate: (code) => api.post('/invites/validate', { code }),
};

// Reputation endpoints
export const reputationAPI = {
  getMe: () => api.get('/reputation/me'),
  getMyReputation: () => api.get('/reputation/me'), // alias
  getMyChanges: () => api.get('/reputation/me'),    // alias
  getLeaderboard: (type) =>
    api.get(`/leaderboards/${type}`),
};

// In-app notifications (KAN-533). The judgment pipeline writes them; the header badge and
// /notifications read them.
export const notificationsAPI = {
  list: (params = {}) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
};

// Share card (KAN-24/25)
export const shareAPI = {
  getMyCard: () => api.get('/share/card'),
  // Public routes: pass the opaque public_id. The numeric id is refused (404).
  getUserCard: (publicId) => api.get(`/share/users/${publicId}/card`),
  // KAN-125: public Instagram result image (save & post).
  instagramImageUrl: (publicId) => `/api/share/users/${publicId}/instagram.svg`,
};

// Capability leaderboard + unified output (KAN-23/27) + stored result (KAN-125)
export const capabilityAPI = {
  myLeaderboard: () => api.get('/capability/leaderboard/me'),
  userLeaderboard: (userId) => api.get(`/capability/users/${userId}/leaderboard`),
  myOutput: () => api.get('/capability/me/output'),
  userOutput: (userId) => api.get(`/capability/users/${userId}/output`),
  // KAN-125: persist the evaluated profile + rankings (the Agent's retrieval target).
  storeResult: () => api.post('/capability/me/result'),
  myResult: () => api.get('/capability/me/result'),
};

// KAN-124/182 — the capability agent. THE MISSING CLIENT BINDING.
//
// Both endpoints have existed and worked since KAN-124; nothing in this app called
// them, so the agent's memory loop (load a user's active memories -> include them in
// the model context -> write a new memory from the exchange) could not run for a real
// user, and neither could the sector-capability retrieval that reads the same request.
// Chen's fourth launch module is "memory to memorize user profiles" — this is the
// layer that was missing between a working loop and a reachable one.
//
// `profession` is optional here: the server falls back to the value stored on the
// user's profile, so a caller that does not have it in hand still gets sector
// retrieval. Passing it lets a surface override the stored value for one turn.
export const agentAPI = {
  capabilityChat: ({ message, conversationHistory = [], profession = null }) =>
    api.post('/agent/capability/chat', {
      message,
      conversation_history: conversationHistory,
      profession,
    }),
  evolutionChat: ({ message, conversationHistory = [], profession = null }) =>
    api.post('/agent/capability/evolution/chat', {
      message,
      conversation_history: conversationHistory,
      profession,
    }),
};

// Funnel analytics (KAN-26)
export const analyticsAPI = {
  recordShare: (sessionId = null) => api.post('/analytics/share', { session_id: sessionId }),
  getMetrics: () => api.get('/analytics/metrics'),
};

// SJP endpoints
export const sjpAPI = {
  getMyRecords: (params) => api.get('/sjp/me', { params }),
  getUserRecords: (userId, params) =>
    api.get(`/sjp/users/${userId}`, { params }),
  getRecord: (id) => api.get(`/sjp/${id}`),
};

// Boundary bout content (CCS content_items) — the Content desk's bouts branch.
// Editors list/read/draft; approve + retire are admin (same split as the FC bank).
export const boutContentAPI = {
  list: (params) => api.get('/ccs/admin/content', { params }),
  get: (uid, version) =>
    api.get(`/ccs/admin/content/${encodeURIComponent(uid)}`, version ? { params: { version } } : undefined),
  createVersion: (data) => api.post('/ccs/admin/content', data),
  // KAN-155 map rows joined to what is seeded — the authoring inventory.
  coverage: (params) => api.get('/ccs/admin/curriculum-coverage', { params }),
  // KAN-158: lintReason is the approver's reason for approving past bout-linter review findings.
  approve: (uid, version, lintReason) =>
    api.post(`/ccs/admin/content/${encodeURIComponent(uid)}/versions/${version}/approve`,
      lintReason ? { lint_reason: lintReason } : undefined),
  retire: (uid, version) =>
    api.post(`/ccs/admin/content/${encodeURIComponent(uid)}/versions/${version}/retire`),
};

// KAN-148 (Paul 15316): editors save wording fixes as a new draft; the desk shows what changed.
export const contentEditAPI = {
  textFields: (uid, version) =>
    api.get(`/ccs/admin/content/${encodeURIComponent(uid)}/versions/${version}/text-fields`),
  changes: (uid, version) =>
    api.get(`/ccs/admin/content/${encodeURIComponent(uid)}/versions/${version}/changes`),
  save: (uid, body) => api.post(`/ccs/admin/content/${encodeURIComponent(uid)}/text-edit`, body),
};

// What's live (Paul, Slack 27 Oct): which switches and journeys are on, for whom, and when each
// last changed, worked out by the server the same way it decides. Viewer, editor or admin.
export const whatsLiveAPI = {
  get: () => api.get('/admin/whats-live'),
};

// Admin endpoints
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  // CCS telemetry observability (UAT). since_hours optional (omit = all time).
  getCcsTelemetry: (params) => api.get('/ccs/admin/telemetry/summary', { params }),
  getCcsEngagement: (params) => api.get('/ccs/admin/engagement', { params }),
  getCcsProvingGroundsVolume: (params) => api.get('/ccs/admin/proving-grounds/volume', { params }),
  getUsers: (params) => api.get('/admin/users', { params }),
  listUsers: (params) => api.get('/admin/users', { params }),        // alias
  // KAN-543: a name/email search is personal data, so it travels in a POST body (never in the URL / access log).
  searchUsers: ({ search, page, page_size } = {}) => api.post('/admin/users/search', { search: search || null, page, page_size }),
  // KAN-358 D3 roles surface. Narrow by design: the roster carries id/email/role
  // only, so role management doesn't require the steward-gated PII user list.
  listUserRoles: (params) => api.get('/admin/users/roles', { params }),
  updateUserRole: (id, role) => api.put(`/admin/users/${id}/role`, { role }),
  // Destructive: clears a learner's progress so the flow can be walked again.
  // The email is the confirmation — see the route's docstring for what survives.
  // KAN-560: one participant's explanation (strengths, difficulties, support, next practice), each statement
  // with its support label and the answers it cites. Data stewards only (like the Users list); every read is logged by the server.
  participantExplanation: (id) => api.get(`/admin/learning/explanation/${id}`),
  resetUserProgress: (id, confirm_email) =>
    api.post(`/admin/users/${id}/reset-progress`, { confirm_email }),
  updateUserStatus: (id, status) =>
    api.put(`/admin/users/${id}/status`, { status }),
  getQuestions: (params) => api.get('/admin/questions', { params }),
  listQuestions: (params) => api.get('/admin/questions', { params }), // alias
  createQuestion: (data) => api.post('/admin/questions', data),
  updateQuestion: (id, data) => api.put(`/admin/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/admin/questions/${id}`),
  getNewWords: (params) => api.get('/admin/new-words', { params }),
  listNewWords: (params) => api.get('/admin/new-words', { params }), // alias
  updateNewWordStatus: (id, status) =>
    api.put(`/admin/new-words/${id}/status`, { status }),
  updateWordStatus: (id, status) =>
    api.put(`/admin/new-words/${id}/status`, { status }),            // alias
  getLearningTheories: (params) =>
    api.get('/admin/learning-theories', { params }),
  updateTheoryStatus: (id, status) =>
    api.put(`/admin/learning-theories/${id}/status`, { status }),
  getChallengeAnswers: (params) =>
    api.get('/admin/challenge-answers', { params }),
  updateAnswerStatus: (id, status) =>
    api.put(`/admin/challenge-answers/${id}/status`, { status }),
  // KAN-552: internal test runs entered by hand (Paul / Michael), and the accounts they may use.
  listInternalAccounts: () => api.get('/admin/internal-accounts'),
  listTestRuns: (params) => api.get('/admin/test-runs', { params }),
  createTestRun: (data) => api.post('/admin/test-runs', data),
};

// KAN-259: the tester roster for the seed-user test (role=admin). Upload is a dry run unless
// dry_run is false; removal keeps the row (append-only).
export const testerRosterAPI = {
  list: () => api.get('/admin/testers'),
  upload: (csv, dryRun) => api.post('/admin/testers/upload', { csv, dry_run: dryRun }),
  remove: (id) => api.post(`/admin/testers/${id}/remove`),
};

// Cognitive Conditioning System (KAN-146/147)
// V1.4 Mind Compiler (MC03/MC07): the learner sees, confirms, corrects and withdraws what the
// system understands about how they plan and decide.
export const mindContextAPI = {
  get: () => api.get('/mind-context'),
  set: (key, value) => api.put(`/mind-context/${key}`, { value }),
  confirm: (key) => api.post(`/mind-context/${key}/confirm`),
  withdraw: (key) => api.delete(`/mind-context/${key}`),
  // KAN-560: what the Companion remembered from conversations; the learner keeps or removes each.
  remembered: () => api.get('/mind-context/remembered'),
  keepRemembered: (key) => api.post(`/mind-context/remembered/${key}/keep`),
  removeRemembered: (key) => api.delete(`/mind-context/remembered/${key}`),
};

// KAN-535 — the daily study plan (KAN-330 routes). `negotiate` previews a pace and saves
// nothing; `converse` is one turn of the cadence conversation (stateless: send back the
// `proposal` you were shown as `pending`). A turn the rules cannot read calls the model, so
// the server rate-limits it; a 429 is a normal outcome here.
export const dailyPlanAPI = {
  get: () => api.get('/learning/daily-plan'),
  negotiate: (request) => api.post('/learning/daily-plan/negotiate', { request }),
  converse: (message, pending) =>
    api.post('/learning/daily-plan/conversation', pending ? { message, pending } : { message }),
};

// KAN-324: the course library. A course is offered for the learning plan's focus after Day 7;
// learners see approved courses only (staff also see drafts). Nothing here is scored.
export const coursesAPI = {
  forPlan: () => api.get('/learning/courses/for-plan'),
  get: (courseId) => api.get(`/learning/courses/${encodeURIComponent(courseId)}`),
  lesson: (courseId, lessonId) =>
    api.get(`/learning/courses/${encodeURIComponent(courseId)}/lessons/${encodeURIComponent(lessonId)}`),
  saveResponse: (courseId, lessonId, text) =>
    api.post(`/learning/courses/${encodeURIComponent(courseId)}/lessons/${encodeURIComponent(lessonId)}/response`, { text }),
  complete: (courseId, lessonId) =>
    api.post(`/learning/courses/${encodeURIComponent(courseId)}/lessons/${encodeURIComponent(lessonId)}/complete`),
  // Paul's bridge: the answer to the lesson's closing "Before you go" question; saved, no feedback.
  saveBridge: (courseId, lessonId, text) =>
    api.post(`/learning/courses/${encodeURIComponent(courseId)}/lessons/${encodeURIComponent(lessonId)}/bridge`, { text }),
  asset: (name) => api.get(`/learning/courses/assets/${encodeURIComponent(name)}`, { responseType: 'blob' }),
};

// KAN-324: the courses branch of the content desk (editor or admin): review and approve.
export const courseDeskAPI = {
  list: () => api.get('/ccs/admin/courses'),
  get: (courseId) => api.get(`/ccs/admin/courses/${encodeURIComponent(courseId)}`),
  approve: (courseId, note) => api.post(`/ccs/admin/courses/${encodeURIComponent(courseId)}/approve`, note ? { note } : {}),
  withdraw: (courseId) => api.post(`/ccs/admin/courses/${encodeURIComponent(courseId)}/withdraw`),
};

export const ccsAPI = {
  captureOnboarding: (data) => api.post('/ccs/onboarding', data),
  getOnboarding: () => api.get('/ccs/onboarding'),
  // KAN-241 in-product satisfaction capture.
  submitSatisfaction: (data) => api.post('/ccs/satisfaction', data),
  // KAN-152 interview surface (server is stateless; client keeps conversation_turns).
  interviewTurn: (data) => api.post('/ccs/interview/turn', data),
  // KAN-272 hook for the scripted surface: one adaptive follow-up for
  // (question, answer); null probe -> the client keeps its scripted follow-up.
  interviewProbe: (data) => api.post('/ccs/interview/probe', data),
  interviewFinalize: (data) => api.post('/ccs/interview/finalize', data),
  getConstellation: () => api.get('/ccs/constellation'),
  getFcAttempt: () => api.get('/ccs/fc-attempt'),
  // KAN-243 native in-app diagnostic (flag CCS_NATIVE_DIAGNOSTIC). Server owns the
  // draw + scoring; the client posts selected_option_id and never sees the key.
  startFcAttempt: () => api.post('/ccs/fc-attempt/start'),
  getFcAttemptItems: () => api.get('/ccs/fc-attempt/items'),
  submitFcResponse: (data) => api.post('/ccs/fc-attempt/responses', data),
  completeFcAttempt: () => api.post('/ccs/fc-attempt/complete'),
  getProfile: () => api.get('/ccs/profile'),
  // KAN-273 Boundary landing read model — growth edge + Spark/Focus/Stretch set
  // states + the Proving Grounds gate. Pure read; starts nothing.
  getBoundary: () => api.get('/ccs/boundary'),
  // KAN-148 item 2: `preview` ({ content_uid, content_version }) is the Content desk's "Preview as
  // a learner" on one item and version, drafts included. Editors and admins only (the server
  // ignores it for members); the bout records nothing.
  startBout: (dose_tier, objective_id, preview) => api.post('/ccs/bouts', {
    ...(dose_tier ? { dose_tier } : {}),
    ...(objective_id ? { objective_id } : {}),
    ...(preview?.content_uid ? { content_uid: preview.content_uid } : {}),
    ...(preview?.content_version ? { content_version: preview.content_version } : {}),
  }),
  getBout: (id) => api.get(`/ccs/bouts/${id}`),
  getBoutContent: (id) => api.get(`/ccs/bouts/${id}/content`),
  // KAN-371: the Confidence Anchor Cards pilot (journey days 4-6 and re-checks); never a level.
  setConfidence: (boutId, tag) => api.post(`/ccs/bouts/${boutId}/confidence`, { tag }),
  // KAN-324 (Paul's bridge): the typed recall at the start of practice; recorded only, never scored.
  recall: (boutId, elementId, text) => api.post(`/ccs/bouts/${boutId}/recall`, { element_id: elementId, text }),
  recallSelfCheck: (boutId, elementId, ticks) => api.post(`/ccs/bouts/${boutId}/recall`, { element_id: elementId, ticks }),
  submitAttempt: (id, data) => api.post(`/ccs/bouts/${id}/submit`, data),
  // KAN-555: the AI chat step of a task_then_ai item (bounded to the item; shared agent quota).
  getItemAiChat: (id) => api.get(`/ccs/bouts/${id}/ai-chat`),
  openItemAiChat: (id) => api.post(`/ccs/bouts/${id}/ai-chat/open`),
  sendItemAiChat: (id, message) => api.post(`/ccs/bouts/${id}/ai-chat/message`, { message }),
  // Learning loop step 3: what a typed answer was missing and what to try next. Polled after a
  // typed answer is submitted, until status is no longer 'reading'.
  getTypedAnswerFeedback: (crId) => api.get(`/ccs/constructed-responses/${crId}/feedback`),
  // KAN-517 / V9-DYNAMIC-01: the adaptive follow-up after a main typed answer. The coach asks one
  // question, gives a hint, or ends; at most two rounds; the learner may stop at any time.
  followupNext: (crId) => api.post(`/ccs/constructed-responses/${crId}/followup/next`),
  followupDelivered: (turnId) => api.post(`/ccs/followup-turns/${turnId}/delivered`),
  // KAN-506: aiUseNote is optional and only sent when given (never scored).
  followupReply: (crId, responseText, aiUseNote) =>
    api.post(`/ccs/constructed-responses/${crId}/followup/reply`,
      aiUseNote ? { response_text: responseText, ai_use_note: aiUseNote } : { response_text: responseText }),
  // KAN-560 (Paul's Day 3 runs, item 3): { afterError: true } when the learner stops from the
  // follow-up error, so the server records a technical failure rather than "no gap".
  followupStop: (crId, opts) => (opts && opts.afterError
    ? api.post(`/ccs/constructed-responses/${crId}/followup/stop`, { after_error: true })
    : api.post(`/ccs/constructed-responses/${crId}/followup/stop`)),
  // KAN-555 (part 2): the learning set's optional steps. Help and revise or apply (KAN-506, Paul
  // 14309: the SYSTEM picks; apply returns the item's new case), the second answer (its own record,
  // not counted toward the level), moving on without one, and core task first or postpone.
  startSecondAttempt: (boutId, elementId) =>
    api.post(`/ccs/bouts/${boutId}/second-attempt/start`, { element_id: elementId }),
  submitSecondAttempt: (boutId, elementId, responseText, aiUseNote) =>
    api.post(`/ccs/bouts/${boutId}/second-attempt/submit`, aiUseNote
      ? { element_id: elementId, response_text: responseText, ai_use_note: aiUseNote }
      : { element_id: elementId, response_text: responseText }),
  declineSecondAttempt: (boutId, elementId) =>
    api.post(`/ccs/bouts/${boutId}/second-attempt/decline`, { element_id: elementId }),
  postponeTask: (boutId, elementId, choice) =>
    api.post(`/ccs/bouts/${boutId}/postpone`, { element_id: elementId, choice }),
  // KAN-555 follow-up: open (start or resume) a core task the integrated item lists. The server
  // checks the item lists it, it is approved and it is in the learner's own journey.
  startCoreTask: (boutId, contentUid) =>
    api.post(`/ccs/bouts/${boutId}/core-task/start`, { content_uid: contentUid }),
  telemetry: (data) => api.post('/ccs/telemetry', data),
  // KAN-250 post-challenge growth reveal — the four assembled facts for the
  // just-completed bout (worked_on / why_it_matters / next / verify). Never an
  // improvement claim; verify.state is "pending" | "due" (never "held").
  getGrowthReveal: (boutId) =>
    api.get('/ccs/growth-reveal', boutId ? { params: { bout_id: boutId } } : undefined),
  // KAN-249 Future Opportunity bridge — the six-stage journey + per-dimension
  // "where this leads" map (curated seed today, engine-backed later, same shape).
  // Entries carry content_status; the surface withholds anything not "approved".
  getOpportunityBridge: () => api.get('/ccs/opportunity-bridge'),
  getRecheckEligible: () => api.get('/ccs/recheck/eligible'),
  // KAN-534 — one Proving Grounds session (KAN-230 read model). Owner-scoped: 404 for a session
  // that is not the caller's. Pure read.
  getProvingGrounds: (sessionId) => api.get(`/ccs/proving-grounds/${encodeURIComponent(sessionId)}`),
  startRecheck: (subfacetKey) => api.post(`/ccs/recheck/${subfacetKey}`),
  // KAN-270 — the learner-facing read of the latest plan (headline / next step / why),
  // the surface KAN-265 grades. 404s when the learner has no plan yet.
  getPlanRead: () => api.get('/learning/plan/latest/read'),
  // C-6 (KAN-439) — notices for corrected levels (Paul, 14183): one per level, shown beside it
  // until dismissed, then kept; plus the plan line when a correction rebuilt the plan.
  getCorrectionNotices: () => api.get('/ccs/correction-notices'),
  dismissCorrectionNotice: (id) => api.post(`/ccs/correction-notices/${id}/dismiss`),
  // KAN-537 — outside advisory pointers when the current focus has no approved content
  // (KAN-329). Returns { enabled: false } while external_reach_enabled is off.
  getPlanExternalReach: () => api.get('/learning/plan/latest/external-reach'),
  // KAN-259 — the user test's end-of-test feedback form. GET says whether it is open for the
  // caller (and why not); POST sends the one submission (409 once sent or not open).
  getTestFeedback: () => api.get('/ccs/test-feedback'),
  submitTestFeedback: (data) => api.post('/ccs/test-feedback', data),
  // Day 7 — the Detailed Profile form. GET the form (status, questions, held values, drafts);
  // start records the questions as shown; draft keeps answers in progress (not values); POST
  // submits. Answers are { [questionId]: { text } | { option_ids: [...] } }.
  getDetailedProfile: () => api.get('/profile/detailed'),
  startDetailedProfile: () => api.post('/profile/detailed/start'),
  saveDetailedProfileDraft: (answers) => api.put('/profile/detailed/draft', { answers }),
  submitDetailedProfile: (answers) => api.post('/profile/detailed', { answers }),
  // Detailed Profile v0.3 change 4 (MC03): the review ("Here is what we've understood so far").
  // GET the lines; PUT a line answers its question again (earlier version kept; "not sure"
  // leaves it unknown); DELETE leaves it unknown; confirm builds and marks the first plan (once).
  getDetailedProfileReview: () => api.get('/profile/detailed/review'),
  correctDetailedProfileLine: (lineId, answer) =>
    api.put(`/profile/detailed/review/${encodeURIComponent(lineId)}`, { answer }),
  leaveDetailedProfileLineUnknown: (lineId) =>
    api.delete(`/profile/detailed/review/${encodeURIComponent(lineId)}`),
  confirmDetailedProfile: () => api.post('/profile/detailed/confirm'),
};

// FC UAT build result capture (advisory — client-side scoring, not measurement)
export const fcUatAPI = {
  captureResult: (data) => api.post('/fc/uat-result', data),
  myResults: () => api.get('/fc/uat-result/mine'),
};

// KAN-538: the learning loop's Project step (Minimal Future Builder). Owner-scoped. Moving a
// project to produced_result needs evidence (a link or a note); the server then writes the
// one growth event, output_created.
export const learningProjectsAPI = {
  list: () => api.get('/ccs/projects'),
  capture: (data) => api.post('/ccs/projects', data),
  advance: (projectId, toStage, evidence) =>
    api.post(`/ccs/projects/${projectId}/advance`, { to_stage: toStage, ...(evidence ? { evidence } : {}) }),
};

// KAN-538: the project extracted from a judgment (KAN-142). 404 means none was extracted.
// Shown beside the judgment only while FEATURE_FLAGS.JUDGMENT_SURFACES is on.
// KAN-259 (13770) — the learner's own Opportunity suggestions. Every call 404s while the server's
// OPPORTUNITY_LEARNER_ENABLED is off, and the page and the dashboard notice then show nothing.
export const opportunityLearnerAPI = {
  list: () => api.get('/learner-opportunities/'),
  open: (uid) => api.get(`/learner-opportunities/${encodeURIComponent(uid)}`),
  decide: (uid, data) => api.post(`/learner-opportunities/${encodeURIComponent(uid)}/decision`, data),
  declareConflict: (uid, data) => api.post(`/learner-opportunities/${encodeURIComponent(uid)}/conflict`, data),
  match: (uid, data) => api.post(`/learner-opportunities/${encodeURIComponent(uid)}/match`, data),
};

// KAN-259 — /admin/opportunities (admin-only on the server; independent of the learner flag).
// Create, the four entry fields, projectize and the person role use the existing admin-only
// Opportunity routes; the rest is /api/admin/opportunities.
const oppUid = (uid) => encodeURIComponent(uid);
export const opportunityAdminAPI = {
  list: () => api.get('/admin/opportunities/'),
  get: (uid) => api.get(`/admin/opportunities/${oppUid(uid)}`),
  create: (data) => api.post('/opportunities/', data),
  setDescription: (uid, description) =>
    api.patch(`/admin/opportunities/${oppUid(uid)}/description`, { description }),
  setEntryFields: (uid, fields) => api.patch(`/opportunities/${oppUid(uid)}/entry-fields`, fields),
  flagSecondRanking: (uid, data) =>
    api.post(`/admin/opportunities/${oppUid(uid)}/second-ranking-flag`, data),
  moveLifecycle: (uid, data) => api.post(`/admin/opportunities/${oppUid(uid)}/lifecycle`, data),
  addFit: (uid, data) => api.post(`/admin/opportunities/${oppUid(uid)}/fits`, data),
  removeFit: (uid, data) => api.post(`/admin/opportunities/${oppUid(uid)}/fits/remove`, data),
  projectize: (uid) => api.post(`/opportunities/${oppUid(uid)}/project`, {}),
  addPersonRole: (projectUid, data) =>
    api.post(`/opportunities/projects/${encodeURIComponent(projectUid)}/resource-needs`,
      { kind: 'person', ...data }),
  preview: (data) => api.post('/admin/opportunities/preview', data),
};

// Judgment system (KAN-543 / KAN-548): /api/judgment-system. The whole feature answers 404 while
// the server flag is off. Each model-calling step can take about a minute, so no client timeout.
const jsId = encodeURIComponent;
export const judgmentSystemAPI = {
  config: () => api.get('/judgment-system/config'),
  listRuns: (params = {}) => api.get('/judgment-system/runs', { params }),
  createRun: (data) => api.post('/judgment-system/runs', data),
  getRun: (uid) => api.get(`/judgment-system/runs/${jsId(uid)}`),
  exportRun: (uid) => api.get(`/judgment-system/runs/${jsId(uid)}/export`),
  record: (uid, data) => api.post(`/judgment-system/runs/${jsId(uid)}/record`, data),
  step: (uid, step) => api.post(`/judgment-system/runs/${jsId(uid)}/steps/${step}`),
  humanPlusAi: (uid, data) => api.post(`/judgment-system/runs/${jsId(uid)}/human-plus-ai`, data),
  // The server takes the comparison group inside `population`; the form builds the group itself.
  ranking: (uid, which, population) => api.post(`/judgment-system/runs/${jsId(uid)}/rankings/${which}`, { population }),
  final: (uid, data, propose = false) =>
    api.post(`/judgment-system/runs/${jsId(uid)}/final`, data, propose ? { params: { propose: true } } : undefined),
  reviewChallenge: (uid, data) => api.post(`/judgment-system/runs/${jsId(uid)}/review-challenge`, data),
  reviewResponse: (uid, data) => api.post(`/judgment-system/runs/${jsId(uid)}/review-response`, data),
  pack: (uid, data) => api.post(`/judgment-system/runs/${jsId(uid)}/pack`, data),
  update: (uid, data) => api.post(`/judgment-system/runs/${jsId(uid)}/update`, data),
  // Admin only: the KAN-259 completion flag for the run's owner, read from the platform records.
  completion: (uid) => api.get(`/judgment-system/runs/${jsId(uid)}/completion`),
};

export const judgmentProjectsAPI = {
  getByJudgment: (judgmentId) => api.get(`/projects/by-judgment/${judgmentId}`),
};

// Monitoring consent (opt-in gate before interaction monitoring)
export const consentAPI = {
  getStatus: (consent_type = 'interaction_monitoring') =>
    api.get('/consent', { params: { consent_type } }),
  setConsent: (data) => api.post('/consent', data),
};

// Participation / guardian consent (KAN-150.B — who may participate)
export const participationAPI = {
  getStatus: () => api.get('/participation'),
  requestGuardian: (data) => api.post('/participation/guardian-request', data),
  // guardian-facing, token-authed (used by the approval link, not the learner)
  recordDecision: (data) => api.post('/participation/guardian-decision', data),
  withdraw: () => api.post('/participation/withdraw'),
  rejoin: () => api.post('/participation/rejoin'),
};

// New Concepts Competition (COMP). Every route 404s while COMPETITION_ENABLED is off on the
// server; the pages treat 404 as "the competition does not exist yet" and send the visitor home.
export const competitionAPI = {
  info: () => api.get('/competition'),
  me: () => api.get('/competition/me'),
  register: (data) => api.post('/competition/registration', data),
  withdraw: () => api.post('/competition/registration/withdraw'),
  // multipart: `fields` is the entry as JSON text, `file` the optional evidence upload
  submit: (fields, file) => {
    const form = new FormData();
    form.append('fields', JSON.stringify(fields));
    if (file) form.append('file', file);
    return api.post('/competition/submissions', form);
  },
  submission: (publicId) => api.get(`/competition/submissions/${encodeURIComponent(publicId)}`),
  submissionFile: (publicId) =>
    api.get(`/competition/submissions/${encodeURIComponent(publicId)}/file`, { responseType: 'blob' }),
};

// KAN-574: the admin competition page (/admin/competition). `status` answers whether or not
// COMPETITION_ENABLED is on; the lists, CSVs and file download 404 while it is off.
export const competitionAdminAPI = {
  status: () => api.get('/admin/competition-status'),
  registrations: (params) => api.get('/admin/competition/registrations', { params }),
  submissions: (params) => api.get('/admin/competition/submissions', { params }),
  registrationsCsv: () => api.get('/admin/competition/registrations.csv', { responseType: 'blob' }),
  submissionsCsv: () => api.get('/admin/competition/submissions.csv', { responseType: 'blob' }),
  submissionFile: (publicId) =>
    api.get(`/admin/competition/submissions/${encodeURIComponent(publicId)}/file`, { responseType: 'blob' }),
};

// Answer chain (KAN-335/336) — the tester-facing surface over POST /api/answer.
// While the chain is off the endpoint 404s (gate: answer_chain_enabled), so the UI must
// treat 404 as "not enabled yet", not an error to surface to the tester.
export const answerAPI = {
  ask: (question, task = null) => api.post('/answer', { question, task }),
  feedback: (request_id, feedback) => api.post(`/answer/${request_id}/feedback`, { feedback }),
  // KAN-537 — both 404 while their server flags are off (answer_clarify_enabled,
  // external_reach_enabled); callers treat 404 as "not switched on".
  clarify: (question, term = null) => api.post('/answer/clarify', { question, term }),
  externalReach: (subfacet) => api.post('/answer/external-reach', { subfacet }),
  externalReachStatus: () => api.get('/answer/external-reach/status'), // { enabled } only
};

// Admin → Content: the FC assessment bank editor (versioned; every save is a new
// draft version, approval is separate, `frozen` refuses writes).
export const fcBankAdminAPI = {
  list: () => api.get('/ccs/admin/fc-bank'),
  get: (itemId) => api.get(`/ccs/admin/fc-bank/${encodeURIComponent(itemId)}`),
  createVersion: (itemId, spec, newItem = false) =>
    api.post(`/ccs/admin/fc-bank/${encodeURIComponent(itemId)}/versions`, { spec, new_item: newItem }),
  approve: (itemId, version) =>
    api.post(`/ccs/admin/fc-bank/${encodeURIComponent(itemId)}/versions/${version}/approve`),
  retire: (itemId, version) =>
    api.post(`/ccs/admin/fc-bank/${encodeURIComponent(itemId)}/versions/${version}/retire`),
  seed: () => api.post('/ccs/admin/fc-bank/seed'),
  getFrozen: () => api.get('/ccs/admin/fc-bank/frozen'),
  setFrozen: (enabled, note) => api.post('/ccs/admin/fc-bank/frozen', { enabled, note }),
};

// Flare short-set sessions (KAN-279) - one POST per session; the store is
// idempotent on flare_session_id (see pages/FlareSessionPage.js for the rule).
export const flareAPI = {
  createSession: (data) => api.post('/flare/sessions', data),
  // KAN-491. Claim a session that was stored anonymously (13+ asserted at the doors)
  // once an account exists. Idempotent server-side, and never re-points a session that
  // already belongs to someone else.
  linkSession: (id) => api.post(`/flare/sessions/${id}/link`),
  getSession: (id) => api.get(`/flare/sessions/${id}`),
  // Current user's persisted sessions, newest first — the dashboard's "first
  // experience done" fact and the /learn/analysis readout source.
  mySessions: () => api.get('/flare/sessions/mine'),
  // The six-day entry experience (Mr. Chen item 4): which day the learner may do
  // next, and how far behind they are. Pure read — loading it completes nothing.
  entryProgress: () => api.get('/flare/entry-progress'),
  // KAN-488 option 1: public, no account needed — whether visitors sign up first.
  entryConfig: () => api.get('/flare/entry-config'),
  // L-6: start (or resume) planned entry day N; returns the bout the runner plays.
  startEntryDay: (day) => api.post(`/flare/entry-days/${day}/start`),
};

// Short-name aliases used by page components
export const auth = authAPI;
export const users = usersAPI;
export const tests = testsAPI;
export const newWords = newWordsAPI;
export const learningTheories = learningTheoriesAPI;
export const challenges = challengesAPI;
export const invites = invitesAPI;
export const reputation = reputationAPI;
export const sjp = sjpAPI;
export const admin = adminAPI;
export const answer = answerAPI;
export const flare = flareAPI;

export default api;
